// src/common/Documents/SearchBar/SearchBar.js

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  IconButton,
  InputAdornment,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useLanguage } from "context/LanguageContext";

const SearchBar = ({
  onSearch,
  placeholder,
  initialQuery,
  initialField,
  initialExact,
  filterButton,
  filterChips,
  trailingActions,
}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery || "");
  const [searchField, setSearchField] = useState(initialField || "all");
  const [exactMatch, setExactMatch] = useState(!!initialExact);
  const { t } = useLanguage();

  // Define search field options
  const searchFieldOptions = [
    { value: "all", label: t("allFields") || "All Fields" },
    { value: "title", label: t("documentTitle") || "Document Title" },
    { value: "supplier_name", label: t("supplierName") || "Supplier Name" },
    { value: "supplier_registration", label: t("supplierRegistration") || "Supplier Registration" },
    { value: "receiver_name", label: t("receiverName") || "Receiver Name" },
    { value: "receiver_registration", label: t("receiverRegistration") || "Receiver Registration" },
    { value: "invoice_id", label: t("invoiceId") || "Invoice ID" },
    { value: "invoice_date", label: t("invoiceDate") || "Invoice Date" },
    { value: "due_date", label: t("dueDate") || "Due Date" },
    { value: "currency", label: t("currency") || "Currency" },
    { value: "supplier_address", label: t("supplierAddress") || "Supplier Address" },
    { value: "supplier_iban", label: t("supplierIban") || "Supplier IBAN" },
    { value: "net_amount", label: t("netAmount") || "Net Amount" },
    { value: "total_tax_amount", label: t("totalTaxAmount") || "Total Tax Amount" },
    { value: "total_amount", label: t("totalAmount") || "Total Amount" },
    { value: "supplier_tax_id", label: t("supplierTaxId") || "Supplier Tax ID" },
    { value: "receiver_tax_id", label: t("receiverTaxId") || "Receiver Tax ID" },
    { value: "recognized_text", label: t("recognizedText") || "Recognized Text" },
    { value: "unit_price", label: t("unitPrice") || "Unit Price" },
  ];

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleFieldChange = (event) => {
    setSearchField(event.target.value);
  };

  const handleExactMatchChange = (event) => {
    setExactMatch(event.target.checked);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    // Send search query, field, and exact match option to parent component
    onSearch({
      query: searchQuery.trim(),
      field: searchField,
      exactMatch: exactMatch,
    });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchField("all");
    setExactMatch(false);
    onSearch({
      query: "",
      field: "all",
      exactMatch: false,
    });
  };

  // Keep UI in sync when parent provides saved search filters (e.g., after navigation back)
  useEffect(() => {
    setSearchQuery(initialQuery || "");
    setSearchField(initialField || "all");
    setExactMatch(!!initialExact);
  }, [initialQuery, initialField, initialExact]);

  return (
    <Box display="flex" flexDirection="column" gap={1} width="100%">
      <Box
        display="flex"
        gap={1}
        width="100%"
        alignItems="center"
        flexDirection={{ xs: "column", sm: "row" }}
      >
        {/* Search Field Selector */}
        <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <InputLabel>{t("searchIn") || "Search in"}</InputLabel>
          <Select
            value={searchField}
            onChange={handleFieldChange}
            label={t("searchIn") || "Search in"}
          >
            {searchFieldOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Search Input */}
        <form onSubmit={handleSearchSubmit} style={{ flexGrow: 1, width: "100%" }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder={
              searchField === "all"
                ? placeholder || t("searchDocuments")
                : `${t("searchIn") || "Search in"} ${
                    searchFieldOptions.find((opt) => opt.value === searchField)?.label
                  }`
            }
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery ? (
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon />
                    </IconButton>
                  ) : (
                    <IconButton size="small" onClick={handleSearchSubmit} edge="end">
                      <SearchIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </form>

        {/* Filter Button */}
        {filterButton && (
          <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}>{filterButton}</Box>
        )}
      </Box>

      {/* Active Options / Filter Chips row */}
      {(searchField !== "all" || initialQuery || filterChips || trailingActions) && (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" pt={0.25}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{ flex: "1 1 auto", minWidth: 0 }}
          >
            {/* Exact Match Option - only show when a specific field is selected */}
            {searchField !== "all" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exactMatch}
                    onChange={handleExactMatchChange}
                    size="small"
                    color="primary"
                  />
                }
                label={t("exactMatch") || "Exact match"}
                sx={{ margin: 0 }}
              />
            )}

            {/* Show active search indicator */}
            {initialQuery && initialField !== "all" && (
              <Chip
                label={`${t("searchingIn") || "Searching in"}: ${
                  searchFieldOptions.find((opt) => opt.value === initialField)?.label
                } · “${initialQuery}” ${
                  initialExact
                    ? `(${t("exactMatch") || "exact"})`
                    : `(${t("contains") || "contains"})`
                }`}
                size="small"
                onDelete={handleClearSearch}
                color="primary"
                variant="outlined"
              />
            )}

            {/* Show active search indicator for "all fields" search */}
            {initialQuery && initialField === "all" && (
              <Chip
                label={`${t("searchingIn") || "Searching in"}: ${
                  t("allFields") || "All Fields"
                } · “${initialQuery}”`}
                size="small"
                onDelete={handleClearSearch}
                color="primary"
                variant="outlined"
              />
            )}

            {/* Active filter chips from FilterBar */}
            {filterChips}
          </Box>
          {trailingActions && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              sx={{ ml: { xs: 0, md: "auto" }, width: { xs: "100%", sm: "auto" } }}
            >
              {trailingActions}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

SearchBar.propTypes = {
  onSearch: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  initialQuery: PropTypes.string,
  initialField: PropTypes.string,
  initialExact: PropTypes.bool,
  filterButton: PropTypes.node,
  filterChips: PropTypes.node,
  trailingActions: PropTypes.node,
};

SearchBar.defaultProps = {
  placeholder: "",
  initialQuery: "",
  initialField: "all",
  initialExact: false,
  filterButton: null,
  filterChips: null,
  trailingActions: null,
};

export default SearchBar;
